import type { ReactNode } from "react";

import { cn } from "../ui/cn";

// The grid of the Home dashboard (CHT-2020). Four places, in the order of the Paper mock-up:
//
//   [ hero  ] [ stats                      ]     `hero`  the dark "Waiting on a person" card (HomeHero)
//   [ needs you now          ] [ side      ]     `needs` the "Needs you now" list (NeedsYouNow)
//
// `stats` and `side` are slots for the next tickets: `stats` for the stat tiles with trends (CHT-2025: three cards beside the hero) and
// `side` for team load and the "no cover" alert (CHT-2026: a column beside the list). While a slot is empty the grid draws no gap for it:
// the hero spans the row and the list spans the width. A ticket fills a slot by passing its node from `HomePage`; this file does not change.
// Below the `lg` width the places stack in the same order.

type HomeLayoutProps = Readonly<{
  /** Told whether the hero spans the row (`wide`: no `stats` beside it). `null`: the role has no such card. */
  hero: (place: Readonly<{ wide: boolean }>) => ReactNode;
  needs: ReactNode;
  stats?: ReactNode;
  side?: ReactNode;
}>;

export function HomeLayout({ hero, needs, stats, side }: HomeLayoutProps) {
  const hasStats = stats !== undefined && stats !== null;
  const hasSide = side !== undefined && side !== null;
  return (
    <div className="grid gap-5">
      <HeroRow hero={hero({ wide: !hasStats })} stats={hasStats ? stats : null} />
      <div
        className={cn("grid items-start gap-5", hasSide && "xl:grid-cols-[minmax(0,1fr)_340px]")}
      >
        {needs}
        {hasSide ? <div className="grid content-start gap-4">{side}</div> : null}
      </div>
    </div>
  );
}

function HeroRow({ hero, stats }: Readonly<{ hero: ReactNode; stats: ReactNode }>) {
  if (hero === null && stats === null) return null;
  return (
    <div
      className={cn(
        "grid gap-3",
        hero !== null && stats !== null && "lg:grid-cols-[420px_minmax(0,1fr)]",
      )}
    >
      {hero}
      {stats}
    </div>
  );
}
